import axios from 'src/axios';
import { HPO_CONSTANTS } from 'src/components/Constants';
import {
  readTerminologyAvailability,
  terminologyAvailabilityFromError
} from 'src/util/terminologyAvailability';
import { geneIdsOf, geneKeyOf } from './testRecordModel';

// The gene lookup the Test form already uses (GET /terminology/network/search/gene, HGNC), for
// adding genes tested (TGCLOUD-1866, Phase 2 rework).
//
// A PASTED SYMBOL IS FOUND ONLY BY AN EXACT MATCH, by the rule the server resolves a gene with
// (documentExtraction/terminologyClient.resolveGene, genotypeVariantService.resolveGeneNcbiId):
// the head of a result's label, "BRCA2 — BRCA2 DNA repair associated", must BE the symbol. The
// search is ranked, so its first result for "BRCA2" may be BRCA2-OT1, and "found" must never mean
// "something like it was found".
//
// NOTHING IS DROPPED. A symbol the lookup does not know is answered `not-found`, and a lookup that
// could not run (switched off, unreachable, an empty vocabulary: the answer says so with
// `available: false`, TGCLOUD-1785) answers `unchecked` with the reason, so the dialog shows both
// rather than losing them. The save keeps an unresolved symbol with no ids (a lab may print a
// symbol HGNC has since retired), so the clinician decides.

const SEARCH_URL = `${
  HPO_CONSTANTS.hpoBaseURL
}${HPO_CONSTANTS.searchHPOTerm.replace(
  ':category',
  HPO_CONSTANTS.category.gene
)}`;

// A pasted panel may name hundreds of genes; this many lookups are in flight at once, so a
// 500-gene list neither queues behind the browser's per-host limit nor floods the service.
export const LOOKUP_CONCURRENCY = 6;

/** The head of a gene lookup label: "BRCA2 — BRCA2 DNA repair associated" -> "BRCA2". */
export const labelSymbol = (name) =>
  String(name || '')
    .split(' — ')[0]
    .trim();

/** A lookup result as a gene: its symbol, label and ids. */
export const geneOfOption = (option) => ({
  symbol: labelSymbol(option?.name),
  name: option?.name || '',
  ...geneIdsOf(option?.id)
});

/**
 * One search of the gene lookup.
 *
 * @returns {Promise<{ options: Array<{ id, name }>, unavailable: object|null }>}
 */
export async function searchGenes(
  term,
  { limit = HPO_CONSTANTS.maxLimit } = {}
) {
  try {
    const { data } = await axios.get(SEARCH_URL, {
      params: { q: term, page: HPO_CONSTANTS.initialPage, limit }
    });
    const unavailable = readTerminologyAvailability(data);
    const options = (Array.isArray(data?.results) ? data.results : []).map(
      (d) => ({ id: d.id, name: d.name })
    );
    return { options, unavailable };
  } catch (err) {
    return { options: [], unavailable: terminologyAvailabilityFromError(err) };
  }
}

/** `fn` over `items`, at most `limit` at a time, answers in the order of `items`. */
export async function mapWithConcurrency(items, limit, fn) {
  const out = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next;
      next += 1;
      out[i] = await fn(items[i], i);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, worker)
  );
  return out;
}

/**
 * Each symbol, looked up exactly.
 *
 * @param {string[]} symbols gene keys (upper case)
 * @returns {Promise<{ results: Array<{ symbol, status: 'found'|'not-found'|'unchecked', name?,
 *   approved?, hgncId?, ncbiGeneId? }>, unavailable: object|null }>} `symbol` is the key asked
 *   for, `approved` HGNC's own spelling of it (C9orf72), and `unavailable` the first reason a
 *   lookup could not run, for the dialog to say
 */
export async function resolveGeneSymbols(
  symbols,
  { concurrency = LOOKUP_CONCURRENCY } = {}
) {
  let unavailable = null;
  const results = await mapWithConcurrency(
    symbols,
    concurrency,
    async (symbol) => {
      const answer = await searchGenes(symbol, { limit: 25 });
      if (answer.unavailable) {
        unavailable = unavailable || answer.unavailable;
        return { symbol, status: 'unchecked' };
      }
      const hit = answer.options.find(
        (o) => geneKeyOf(labelSymbol(o.name)) === symbol
      );
      if (!hit) return { symbol, status: 'not-found' };
      const gene = geneOfOption(hit);
      return {
        symbol,
        status: 'found',
        name: gene.name,
        approved: gene.symbol,
        hgncId: gene.hgncId,
        ncbiGeneId: gene.ncbiGeneId
      };
    }
  );
  return { results, unavailable };
}
